/* SMD reviews block ("From the trail").
   Loads customer photos and the most helpful reviews from Okendo's public
   storefront API (the same one Okendo's own widget calls) and fills every
   [data-smd-reviews] block on the page (desktop + mobile copies).
   Selection: 4-5 stars, at least MIN_BODY characters, at least as many
   helpful votes as unhelpful, sorted by most helpful; first MAX_FEATURED. */
(() => {
  if (window.smdReviewsLoaded) return;
  window.smdReviewsLoaded = true;

  const API = 'https://api.okendo.io/v1';
  const MIN_BODY = 120;
  const MAX_FEATURED = 5;
  const TILES = 4;
  const PAGE = 24;

  const roots = Array.from(document.querySelectorAll('[data-smd-reviews]'));
  if (!roots.length) return;

  const make = (tag, cls, text) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text != null) el.textContent = text;
    return el;
  };
  const getJSON = async (url) => {
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`Okendo ${res.status}`);
    return res.json();
  };
  const absolute = (url) => (/^https?:/.test(url) ? url : API + url);
  const clean = (s) => String(s || '').replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  const monthYear = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const first = roots[0];
  const store = first.dataset.store;
  const productBase = `${API}/stores/${encodeURIComponent(store)}/products/${encodeURIComponent(first.dataset.product)}`;
  const data = {
    featured: [],
    media: [],
    mediaNext: null,
    mediaTotal: Number(first.dataset.mediaCount) || 0,
  };
  const reviewCache = new Map();

  /* ---------- Rendering helpers ---------- */
  const starsEl = (rating) => {
    const wrap = make('span', 'smd-reviews__stars');
    const stars = make('span', 'smd-stars');
    stars.setAttribute('aria-hidden', 'true');
    stars.style.setProperty('--smd-rating', `${Math.max(0, Math.min(5, Number(rating) || 0)) * 20}%`);
    stars.append(make('span', 'smd-stars__base', '\u2605\u2605\u2605\u2605\u2605'), make('span', 'smd-stars__fill', '\u2605\u2605\u2605\u2605\u2605'));
    wrap.append(stars, make('span', 'smd-visually-hidden', `Rated ${rating} out of 5`));
    return wrap;
  };

  let uid = 0;
  const reviewEl = (r, clamp) => {
    const box = make('article', 'smd-reviews__review');
    box.append(starsEl(r.rating));

    const title = clean(r.title);
    if (title) box.append(make('h3', 'smd-reviews__quote', title));

    const body = make('p', 'smd-reviews__body', clean(r.body));
    body.id = `smd-review-body-${(uid += 1)}`;
    box.append(body);

    if (clamp) {
      body.classList.add('smd-reviews__body--clamped');
      const more = make('button', 'smd-reviews__more', 'Read full review');
      more.type = 'button';
      more.hidden = true;
      more.setAttribute('aria-controls', body.id);
      more.setAttribute('aria-expanded', 'false');
      more.addEventListener('click', () => {
        const open = !body.classList.toggle('smd-reviews__body--clamped');
        more.setAttribute('aria-expanded', String(open));
        more.textContent = open ? 'Show less' : 'Read full review';
      });
      box.append(more);
      // Measure when the block becomes visible (it starts hidden, and one copy
      // is display:none at each breakpoint).
      const measure = () => {
        if (!body.classList.contains('smd-reviews__body--clamped')) return;
        more.hidden = body.scrollHeight <= body.clientHeight + 1;
      };
      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(body);
      else requestAnimationFrame(measure);
    }

    const by = make('p', 'smd-reviews__by');
    by.append(make('strong', null, clean(r.reviewer && r.reviewer.displayName) || 'Customer'));
    const bits = [];
    if (r.reviewer && r.reviewer.isVerified) bits.push('Verified buyer');
    (r.productAttributes || []).forEach((a) => {
      const v = Array.isArray(a.value) ? a.value.join(', ') : a.value;
      if (v) bits.push(v);
    });
    const when = monthYear(r.dateCreated);
    if (when) bits.push(when);
    if (bits.length) by.append(` \u00b7 ${bits.join(' \u00b7 ')}`);
    box.append(by);

    if (r.productVariantName) {
      box.append(make('p', 'smd-reviews__bought', `Bought: ${r.productVariantName.split(' / ').join(' \u00b7 ')}`));
    }
    return box;
  };

  const thumbUrl = (m, size) => {
    if (m.type === 'video') return (m.videoUrls && (m.videoUrls.videoPosterUrl || m.videoUrls.thumbnailUrl)) || '';
    if (m.dynamicImageKey) return `https://media-dynamic.okendo.io/images/${m.dynamicImageKey}.jpg?d=${size}x${size}&crop=center`;
    return (m.imageUrls && (m.imageUrls.largeUrl || m.imageUrls.thumbnailUrl)) || '';
  };
  const mediaTotal = () => (data.mediaNext ? Math.max(data.mediaTotal, data.media.length) : data.media.length);

  const loadMoreMedia = async () => {
    if (!data.mediaNext) return;
    const next = data.mediaNext;
    data.mediaNext = null;
    try {
      const page = await getJSON(absolute(next));
      data.media.push(...(page.media || []).filter((m) => thumbUrl(m, 400)));
      data.mediaNext = page.nextUrl || null;
    } catch (e) {
      console.warn('[smd-reviews]', e);
    }
  };

  const getReview = async (id) => {
    if (!reviewCache.has(id)) {
      reviewCache.set(id, getJSON(`${API}/stores/${encodeURIComponent(store)}/reviews/${encodeURIComponent(id)}`).then((j) => j.review));
    }
    return reviewCache.get(id);
  };

  /* ---------- Lightbox (one per page) ---------- */
  const arrowSvg = (d) => `<svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="${d}" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const lightbox = (() => {
    let dlg; let frame; let pos; let side; let prev; let next;
    let idx = 0;
    let token = 0;

    const build = () => {
      dlg = make('dialog', 'smd-dialog smd-reviews-lb');
      dlg.setAttribute('aria-label', 'Customer photos and reviews');
      const close = make('button', 'smd-reviews-lb__close');
      close.type = 'button';
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<span aria-hidden="true">&times;</span>';
      close.addEventListener('click', () => dlg.close());

      const grid = make('div', 'smd-reviews-lb__grid');
      const media = make('div', 'smd-reviews-lb__media');
      frame = make('div', 'smd-reviews-lb__frame');
      prev = make('button', 'smd-reviews-lb__arrow smd-reviews-lb__arrow--prev');
      prev.type = 'button';
      prev.setAttribute('aria-label', 'Previous photo');
      prev.innerHTML = arrowSvg('M10 3 5 8l5 5');
      next = make('button', 'smd-reviews-lb__arrow smd-reviews-lb__arrow--next');
      next.type = 'button';
      next.setAttribute('aria-label', 'Next photo');
      next.innerHTML = arrowSvg('m6 3 5 5-5 5');
      prev.addEventListener('click', () => show(idx - 1));
      next.addEventListener('click', () => show(idx + 1));
      media.append(frame, prev, next);

      side = make('div', 'smd-reviews-lb__side');
      pos = make('p', 'smd-reviews-lb__pos');
      pos.setAttribute('aria-live', 'polite');
      side.append(pos, make('div', 'smd-reviews-lb__review'));
      grid.append(media, side);
      dlg.append(close, grid);

      dlg.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' && !prev.disabled) { e.preventDefault(); show(idx - 1); }
        if (e.key === 'ArrowRight' && !next.disabled) { e.preventDefault(); show(idx + 1); }
      });
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
      dlg.addEventListener('close', () => { const v = frame.querySelector('video'); if (v) v.pause(); });

      (first.closest('.smd-pdp') || document.body).append(dlg);
    };

    const show = async (i) => {
      if (i >= data.media.length && data.mediaNext) await loadMoreMedia();
      const total = mediaTotal();
      idx = Math.max(0, Math.min(i, data.media.length - 1));
      const m = data.media[idx];
      if (!m) return;
      const mine = (token += 1);

      frame.replaceChildren();
      if (m.type === 'video' && m.videoUrls) {
        const v = make('video');
        v.controls = true;
        v.playsInline = true;
        v.preload = 'metadata';
        v.poster = m.videoUrls.videoPosterUrl || m.videoUrls.thumbnailUrl || '';
        v.src = m.videoUrls.fullSizeUrl;
        v.setAttribute('aria-label', 'Customer video');
        frame.append(v);
      } else {
        const img = make('img');
        img.src = (m.imageUrls && m.imageUrls.largeUrl) || thumbUrl(m, 1600);
        img.alt = 'Customer photo';
        frame.append(img);
      }
      pos.textContent = `${m.type === 'video' ? 'Video' : 'Photo'} ${idx + 1} of ${total}`;
      prev.disabled = idx === 0;
      next.disabled = idx >= total - 1;

      const slot = side.querySelector('.smd-reviews-lb__review');
      slot.replaceChildren(make('p', 'smd-reviews-lb__status', 'Loading review\u2026'));
      try {
        const r = m.reviewId ? await getReview(m.reviewId) : null;
        if (mine !== token) return;
        slot.replaceChildren(r ? reviewEl(r, false) : make('p', 'smd-reviews-lb__status', ''));
      } catch (e) {
        if (mine === token) slot.replaceChildren(make('p', 'smd-reviews-lb__status', 'This review couldn\u2019t be loaded.'));
      }
    };

    return {
      open(i) {
        if (!dlg) build();
        if (!dlg.open) dlg.showModal();
        show(i);
      },
    };
  })();

  /* ---------- Fill one block ---------- */
  const fill = (root) => {
    const photos = root.querySelector('[data-smd-reviews-photos]');
    if (data.media.length) {
      const total = mediaTotal();
      const shown = data.media.slice(0, TILES);
      shown.forEach((m, i) => {
        const li = make('li');
        const btn = make('button', `smd-reviews__tile${m.type === 'video' ? ' smd-reviews__tile--video' : ''}`);
        btn.type = 'button';
        const img = make('img');
        img.src = thumbUrl(m, 400);
        img.alt = '';
        img.width = 400;
        img.height = 400;
        img.loading = 'lazy';
        img.decoding = 'async';
        btn.append(img);
        const isLast = i === shown.length - 1 && total > TILES;
        if (isLast) {
          const count = make('span', 'smd-reviews__count');
          count.append(make('span', 'smd-reviews__count-num', `+${total - (TILES - 1)}`), make('span', null, 'more'));
          btn.append(count);
          btn.setAttribute('aria-label', `See all ${total} customer photos and videos`);
        } else {
          btn.setAttribute('aria-label', `Customer ${m.type === 'video' ? 'video' : 'photo'} ${i + 1} of ${total}`);
        }
        btn.addEventListener('click', () => lightbox.open(i));
        li.append(btn);
        photos.append(li);
      });
      photos.hidden = false;
    }

    const featured = root.querySelector('[data-smd-reviews-featured]');
    const list = data.featured;
    if (list.length) {
      const slide = root.querySelector('[data-smd-reviews-slide]');
      const nav = root.querySelector('[data-smd-reviews-nav]');
      const posEl = root.querySelector('[data-smd-reviews-pos]');
      const prev = root.querySelector('[data-smd-reviews-prev]');
      const next = root.querySelector('[data-smd-reviews-next]');
      let idx = 0;
      const render = () => {
        slide.replaceChildren(reviewEl(list[idx], true));
        posEl.textContent = `${idx + 1} of ${list.length} most helpful`;
        prev.disabled = idx === 0;
        next.disabled = idx === list.length - 1;
      };
      prev.addEventListener('click', () => { if (idx > 0) { idx -= 1; render(); } });
      next.addEventListener('click', () => { if (idx < list.length - 1) { idx += 1; render(); } });
      nav.hidden = list.length < 2;
      render();
      featured.hidden = false;
    }

    root.hidden = !(data.media.length || list.length);
  };

  /* ---------- Load ---------- */
  const load = async () => {
    const [reviews, media] = await Promise.allSettled([
      getJSON(`${productBase}/reviews?limit=25&orderBy=${encodeURIComponent('helpful desc')}`),
      getJSON(`${productBase}/review_media?limit=${PAGE}`),
    ]);
    if (reviews.status === 'fulfilled') {
      const all = reviews.value.reviews || [];
      all.forEach((r) => reviewCache.set(r.reviewId, Promise.resolve(r)));
      data.featured = all
        .filter((r) => r.rating >= 4
          && clean(r.body).length >= MIN_BODY
          && (r.helpfulCount || 0) >= (r.unhelpfulCount || 0))
        .slice(0, MAX_FEATURED);
    } else {
      console.warn('[smd-reviews]', reviews.reason);
    }
    if (media.status === 'fulfilled') {
      data.media = (media.value.media || []).filter((m) => thumbUrl(m, 400));
      data.mediaNext = media.value.nextUrl || null;
    } else {
      console.warn('[smd-reviews]', media.reason);
    }
    roots.forEach(fill);
  };

  const start = () => load().catch((e) => console.warn('[smd-reviews]', e));
  if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
  else setTimeout(start, 200);
})();
